/**
 * §7.4 — the four permission rules, in one place.
 *
 * Rule 1: resolve from the server-delivered CapabilitySet only. Nothing here
 *         looks at ownership, rental status or role to decide a control.
 * Rule 2: `absent` means the control is not rendered at all.
 * Rule 3: `granted` renders enabled; `unavailable` renders disabled *with the
 *         reason*, because silently hiding an advertised capability is
 *         indistinguishable from never having granted it.
 * Rule 4: capabilities the connected platform does not support resolve to
 *         `absent`, not `unavailable` — there is no transient condition to wait
 *         out, so a disabled control would be a lie.
 */

import { useMemo } from 'react';
import type { Capability } from '@pm/shared';
import type { Bot, CapabilitySet } from '../api/types';

export type CapabilityState =
  | { state: 'granted' }
  | { state: 'unavailable'; reason: string }
  | { state: 'absent' };

export interface CapabilityResolver {
  set: CapabilitySet | null;
  of: (capability: Capability) => CapabilityState;
  /** Convenience for `of(c).state === 'granted'`; never use it to hide a reason. */
  has: (capability: Capability) => boolean;
  /** True when the control should render at all (granted or unavailable). */
  visible: (capability: Capability) => boolean;
  role: CapabilitySet['role'];
  rentalId: string | null;
}

const ABSENT: CapabilityState = { state: 'absent' };
const GRANTED: CapabilityState = { state: 'granted' };

export function resolveCapability(
  set: CapabilitySet | null,
  capability: Capability,
  platformSupported?: Capability[] | null,
): CapabilityState {
  if (!set) return ABSENT;
  // Rule 4 before anything else: unsupported by the platform is absent.
  if (platformSupported && platformSupported.length > 0 && !platformSupported.includes(capability)) {
    return ABSENT;
  }
  if (set.granted.includes(capability)) return GRANTED;
  const blocked = set.unavailable.find((u) => u.capability === capability);
  if (blocked) return { state: 'unavailable', reason: blocked.reason };
  return ABSENT;
}

export function useCapabilities(bot: Bot | null): CapabilityResolver {
  return useMemo(() => {
    const set = bot?.capabilities ?? null;
    // A platform list is only a filter for capabilities that map to platform
    // features; ownership-level capabilities are never in it.
    //
    // The server sends `adapter_capabilities` as one entry per connected
    // account (routes/bots.ts). A capability counts as platform-supported if
    // ANY connected account's adapter supports it — a bot with a Bluesky and a
    // Reddit account can post so long as one of them can. An earlier revision
    // read a field the API never sent, so this filter never fired against the
    // live server and §7.4 rule 4 was decorative.
    // `supports` arrives as capability names, not adapter support tokens — the
    // server maps the token vocabulary (`upload`) to the capability vocabulary
    // (`upload_asset`) in `adapterCapabilities`, so there is nothing to translate
    // here. Re-mapping through a token table silently dropped every capability
    // whose names differ, which made rule 4 hide controls the platform supports.
    const adapters = bot?.adapter_capabilities ?? null;
    const platformSupported =
      adapters && adapters.length > 0
        ? Array.from(new Set(adapters.flatMap((a) => a.supports)))
        : null;
    /*
     * Which capabilities rule 4 applies to: those whose availability is a
     * property of the connected platform rather than of the rental.
     *
     * `dm` and `read_dm` belong here and were missing, which put a Messages tab
     * on a Bluesky bot — a platform with no DM adapter method at all. The tab
     * led to a screen whose every call would 400, and the server said so only
     * after the click. Rule 4 exists precisely so an impossible control is
     * absent rather than disabled-then-refused.
     *
     * Keep this list in step with `PLATFORM_DERIVED_CAPABILITIES` in
     * apps/api/src/adapters/registry.ts; they are the same set seen from two
     * sides.
     */
    const PLATFORM_SCOPED: Capability[] = [
      'post',
      'comment',
      'read_posts',
      'search',
      'upload_asset',
      'dm',
      'read_dm',
    ];

    const of = (capability: Capability): CapabilityState =>
      resolveCapability(
        set,
        capability,
        PLATFORM_SCOPED.includes(capability) ? platformSupported : null,
      );

    return {
      set,
      of,
      has: (c: Capability) => of(c).state === 'granted',
      visible: (c: Capability) => of(c).state !== 'absent',
      role: set?.role ?? 'none',
      rentalId: set?.rental_id ?? null,
    };
  }, [bot]);
}
